/* Copyright (c) 2017 Backpack Health LLC */
/* eslint-disable no-param-reassign */

import * as _ from 'lodash';

function geneVariantResult(healthEntryState) {
  healthEntryState.gene_variant_result = _.assign(
    {}, {
      gene_variant_present_id: null,
      gene_variant_classification: {},
      gene_mutation_type: {},
      gene_exon_frame_type: {},
      gene_zygosity_type: {},
    },
    healthEntryState.gene_variant_result
  );

  return {
    get: () => healthEntryState.gene_variant_result,
    setVariant: (value) => { healthEntryState.gene_variant_result.gene_variant_present_id = value; },
    setClassification: (value) => {
      if (healthEntryState.gene_variant_result.gene_variant_classification.id !== value) {
        healthEntryState.gene_variant_result.gene_variant_classification.id = value;
      } else {
        healthEntryState.gene_variant_result.gene_variant_classification.id = null;
      }
    },
    setClassificationCui: (value) => {
      if (healthEntryState.gene_variant_result.gene_variant_classification.cui !== value) {
        healthEntryState.gene_variant_result.gene_variant_classification.cui = value;
      } else {
        healthEntryState.gene_variant_result.gene_variant_classification.cui = null;
      }
    },
    setMutationType: (value) => {
      healthEntryState.gene_variant_result.gene_mutation_type.id = value;
    },
    setMutationTypeCui: (value) => {
      healthEntryState.gene_variant_result.gene_mutation_type.cui = value;
    },
    setDNAChange: (value) => {
      healthEntryState.gene_variant_result.gene_dna_change = value || null;
    },
    setProteinChange: (value) => {
      healthEntryState.gene_variant_result.gene_protein_change = value || null;
    },
    setExonRange: (value) => {
      healthEntryState.gene_variant_result.gene_exon_range = value || null;
    },
    setExonFrameType: (value) => {
      healthEntryState.gene_variant_result.gene_exon_frame_type.id = value;
    },
    setExonFrameTypeCui: (value) => {
      healthEntryState.gene_variant_result.gene_exon_frame_type.cui = value;
    },
    setZygosityType: (value) => {
      healthEntryState.gene_variant_result.gene_zygosity_type.id = value;
    },
    setZygosityTypeCui: (value) => {
      healthEntryState.gene_variant_result.gene_zygosity_type.cui = value;
    },

    getVariantPresentId: () => healthEntryState.gene_variant_result.gene_variant_present_id,
    getClassificationId: () => _.get(healthEntryState, 'gene_variant_result.gene_variant_classification.id', null),

    getClassificationCui: () => _.get(healthEntryState, 'gene_variant_result.gene_variant_classification.cui', null),
    getMutationTypeCui: () => _.get(healthEntryState, 'gene_variant_result.gene_mutation_type.cui', null),
    getDNAChange: () => _.get(healthEntryState, 'gene_variant_result.gene_dna_change', null),
    getProteinChange: () => _.get(healthEntryState, 'gene_variant_result.gene_protein_change', null),
    getExonRange: () => _.get(healthEntryState, 'gene_variant_result.gene_exon_range', null),
    getExonFrameTypeCui: () => _.get(healthEntryState, 'gene_variant_result.gene_exon_frame_type.cui', null),
    getZygosityTypeCui: () => _.get(healthEntryState, 'gene_variant_result.gene_zygosity_type.cui', null),

    isDefined: () => !!healthEntryState.gene_variant_result.gene_variant_present_id,
    isAdditionalAttributeDefined: (key, isMedliTerm) => {
      const defaultValue = isMedliTerm ? {} : null;
      const value = _.get(healthEntryState, `gene_variant_result.${key}`, defaultValue);

      return !!(isMedliTerm ? _.get(value, 'cui', null) : value);
    },
    hasGeneticResult: () => !!healthEntryState.gene_variant_result.gene_variant_present_id,

    clear: () => { healthEntryState.gene_variant_result = { gene_variant_present_id: null, gene_variant_classification: {} }; },
    clearAttributeValues: (names) => {
      names.forEach(n => (healthEntryState.gene_variant_result[n] = null));
    },
  };
}

export function cleanGeneVariantResult(healthEntryCategoryState) {
  const result = healthEntryCategoryState.gene_variant_result;
  const variantId = _.get(healthEntryCategoryState, 'gene_variant_result.gene_variant_present_id', '');
  const classificationId = _.get(healthEntryCategoryState, 'gene_variant_result.gene_variant_classification.id', '');
  const mutationTypeId = _.get(healthEntryCategoryState, 'gene_variant_result.gene_mutation_type.id', '');
  const exonFrameTypeId = _.get(healthEntryCategoryState, 'gene_variant_result.gene_exon_frame_type.id', '');
  const zygosityTypeId = _.get(healthEntryCategoryState, 'gene_variant_result.gene_zygosity_type.id', '');

  if (!result || (!variantId && !classificationId)) {
    healthEntryCategoryState = _.omit(healthEntryCategoryState, ['gene_variant_result']);
  } else {
    const omitKeysData = {
      gene_variant_present_id: !variantId,
      gene_variant_classification: classificationId === '' || classificationId === null,
      gene_mutation_type: mutationTypeId === '' || mutationTypeId === null,
      gene_exon_frame_type: exonFrameTypeId === '' || exonFrameTypeId === null,
      gene_zygosity_type: zygosityTypeId === '' || zygosityTypeId === null,
    };
    const filteredOmitKeys = _.compact(
      Object.keys(omitKeysData)
        .map(omitKey => (omitKeysData[omitKey] ? omitKey : ''))
    );

    healthEntryCategoryState.gene_variant_result = _.omit(healthEntryCategoryState.gene_variant_result, filteredOmitKeys);
  }

  return healthEntryCategoryState;
}

export function createGeneVariantResult(geneVariantResultData) {
  return geneVariantResult({ gene_variant_result: geneVariantResultData });
}

export default geneVariantResult;
